// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus, type AccountInfo } from "@azure/msal-browser";
import {
  createLoginRequest,
  createMsalConfig,
  createTokenRequest,
  isAccountForConfiguredAuthority,
} from "../authConfig";
import { navigateTo } from "../router/navigation";
import { replaceHistoryEntry } from "../router/historyIndex";
import { devAccessToken, isDevPreviewMode } from "../utils/devEnv";
import { logAuthFailure } from "../utils/authDiagnostics";
import { resetSignInFailure } from "../utils/signInFailureSignal";
import { consumeReturnTo } from "../routes/returnTo";
import {
  broadcastSignOut,
  hasExplicitSignOut,
  markExplicitSignOut,
  subscribeToSignOutBroadcast,
} from "../features/auth/signOutState";
import {
  notifySessionExpired,
  resetSessionExpired,
  useSessionExpiredSignal,
} from "../utils/sessionExpirySignal";
import {
  attemptInteractiveReauth,
  clearInteractiveReauthAttempt,
  isInteractiveRedirectInFlight,
  isInteractionRequired,
  pendingInteractiveRedirect,
} from "../utils/interactiveReauth";
import { restartSignIn } from "./useRestartSignIn";
import { forgetDraftOwner, noteDraftAccount } from "../utils/draftOwner";
import { clearHeldConnectorCallbacks } from "../utils/connectorCallback";
import {
  clearSignInHandoff,
  handOffToSignInSurface,
  isInteractionInProgressError,
  isOnSignInSurface,
  wasHandedOffThisLoad,
} from "../features/auth/signInHandoff";
import {
  assertAccessTokenForConfiguredApp,
  WrongAppAccessTokenError,
} from "../utils/accessTokenClaims";

function resolvePostLogoutRedirectUri(): string {
  const configured = createMsalConfig().auth.redirectUri;
  if (typeof configured === "string" && configured.length > 0) {
    try {
      return new URL(configured).origin;
    } catch {
    }
  }
  return window.location.origin;
}

export const LOCAL_DEV_SENTINEL_TOKEN = "riddle-local-dev-token";


const ACCOUNT_RESOLVE_TIMEOUT_MS = 1500;
const ACCOUNT_RESOLVE_POLL_MS = 50;

function readCachedAccount(
  instance: ReturnType<typeof useMsal>["instance"],
): AccountInfo | null {
  const withAccountList = instance as unknown as {
    getAllAccounts?: () => AccountInfo[];
  };
  const cached = withAccountList.getAllAccounts?.() ?? [];
  return cached.find(isAccountForConfiguredAuthority) ?? null;
}

async function waitForCachedAccount(
  instance: ReturnType<typeof useMsal>["instance"],
): Promise<AccountInfo | null> {
  const deadline = Date.now() + ACCOUNT_RESOLVE_TIMEOUT_MS;
  let account = readCachedAccount(instance);
  while (account === null && Date.now() < deadline) {
    await new Promise((resolve) => {
      setTimeout(resolve, ACCOUNT_RESOLVE_POLL_MS);
    });
    account = readCachedAccount(instance);
  }
  return account;
}

export function useAuthSession() {
  const { accounts, instance, inProgress } = useMsal();

  const activeAccount = useMemo((): AccountInfo | null => {
    const eligible: AccountInfo[] = accounts.filter(isAccountForConfiguredAuthority);
    const withActive = instance as unknown as {
      getActiveAccount?: () => AccountInfo | null;
    };
    const selected = withActive.getActiveAccount?.() ?? null;
    const match =
      selected === null
        ? undefined
        : eligible.find((account) => account.homeAccountId === selected.homeAccountId);
    return match ?? eligible[0] ?? null;
  }, [accounts, instance]);
  const isAuthenticated = activeAccount !== null;
  const isConfigured = createMsalConfig().auth.clientId.length > 0;

  const activeAccountRef = useRef(activeAccount);
  useEffect(() => {
    activeAccountRef.current = activeAccount;
    noteDraftAccount(activeAccount?.homeAccountId);
  }, [activeAccount]);

  const heldCallbackAccountRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const homeAccountId = activeAccount?.homeAccountId;
    if (homeAccountId === undefined) {
      return;
    }
    if (
      heldCallbackAccountRef.current !== undefined &&
      heldCallbackAccountRef.current !== homeAccountId
    ) {
      clearHeldConnectorCallbacks();
    }
    heldCallbackAccountRef.current = homeAccountId;
  }, [activeAccount]);

  const sessionStartingUpRef = useRef(inProgress === InteractionStatus.Startup);
  useEffect(() => {
    sessionStartingUpRef.current = inProgress === InteractionStatus.Startup;
  }, [inProgress]);

  const login = useCallback(async (): Promise<void> => {
    if (!isOnSignInSurface()) {
      handOffToSignInSurface();
      return;
    }

    resetSessionExpired();
    resetSignInFailure();
    clearInteractiveReauthAttempt();

    const query = new URLSearchParams(window.location.search);
    const loginHint = query.get("login_hint") ?? undefined;
    if (loginHint !== undefined) {
      query.delete("login_hint");
      const queryString = query.toString();
      const nextUrl = queryString
        ? `${window.location.pathname}?${queryString}`
        : window.location.pathname;
      replaceHistoryEntry(nextUrl);
    }

    const loginRequest = createLoginRequest();
    const request = hasExplicitSignOut()
      ? { ...loginRequest, prompt: "select_account" }
      : loginHint !== undefined
        ? { ...loginRequest, loginHint }
        : loginRequest;
    try {
      await instance.loginRedirect(request);
    } catch (error: unknown) {
      if (!isInteractionInProgressError(error) || wasHandedOffThisLoad()) {
        throw error;
      }
      logAuthFailure("login-interaction-in-progress", error);
      handOffToSignInSurface();
    }
  }, [instance]);

  const logout = useCallback(async (): Promise<void> => {
    markExplicitSignOut();
    broadcastSignOut();
    forgetDraftOwner();
    clearHeldConnectorCallbacks();
    clearSignInHandoff();
    const account = activeAccountRef.current;
    try {
      instance.setActiveAccount(null);
    } catch {
    }
    consumeReturnTo();
    resetSignInFailure();
    clearInteractiveReauthAttempt();
    resetSessionExpired();
    try {
      await instance.logoutRedirect({
        ...(account !== null ? { account } : {}),
        postLogoutRedirectUri: resolvePostLogoutRedirectUri(),
      });
      return;
    } catch (error: unknown) {
      logAuthFailure("logout-redirect-failed", error);
    }
    await Promise.resolve()
      .then(() => instance.clearCache())
      .catch(() => {
      });
    navigateTo("/");
  }, [instance]);

  useEffect(
    () =>
      subscribeToSignOutBroadcast(() => {
        forgetDraftOwner();
        clearHeldConnectorCallbacks();
        try {
          instance.setActiveAccount(null);
        } catch {
        }
        void Promise.resolve()
          .then(() => instance.clearCache())
          .catch(() => {
          })
          .finally(() => {
            navigateTo("/");
          });
      }),
    [instance],
  );

  const getAccessToken = useCallback(async (): Promise<string> => {
    const account =
      activeAccountRef.current ??
      readCachedAccount(instance) ??
      (sessionStartingUpRef.current ? await waitForCachedAccount(instance) : null);

    if (isDevPreviewMode && account === null) {
      return devAccessToken ?? LOCAL_DEV_SENTINEL_TOKEN;
    }

    if (account === null) {
      throw new Error("No signed-in account found.");
    }

    const tokenRequest = createTokenRequest();
    try {
      const silentResult = await instance.acquireTokenSilent({
        ...tokenRequest,
        account,
      });
      clearInteractiveReauthAttempt();
      try {
        assertAccessTokenForConfiguredApp(silentResult.accessToken);
      } catch (claimError: unknown) {
        if (claimError instanceof WrongAppAccessTokenError) {
          logAuthFailure("token-wrong-app", claimError);
          if (await restartSignIn(instance)) {
            return await pendingInteractiveRedirect<string>();
          }
          throw claimError;
        }
        throw claimError;
      }
      return silentResult.accessToken;
    } catch (error: unknown) {
      if (isInteractionRequired(error)) {
        if (
          attemptInteractiveReauth(instance, account, tokenRequest) ||
          isInteractiveRedirectInFlight()
        ) {
          return await pendingInteractiveRedirect<string>();
        }
      }

      logAuthFailure("token-acquire-expired", error);
      notifySessionExpired();
      throw new Error("Your session has expired. Please sign in again.", {
        cause: error,
      });
    }
  }, [instance]);

  const expiredSignal = useSessionExpiredSignal();
  useEffect(() => {
    if (expiredSignal && activeAccount !== null) {
      clearHeldConnectorCallbacks();
      instance.setActiveAccount(null);
      void instance.clearCache({ account: activeAccount }).catch(() => {
      });
    }
  }, [expiredSignal, instance, activeAccount]);

  return {
    activeAccount,
    isAuthenticated,
    isConfigured,
    login,
    logout,
    getAccessToken,
  };
}
